<template>
  <v-container>
    <v-data-table
      fixed-header
      :header-props="{ sortIcon: 'fas fa-sort-amount-up-alt' }"
      :headers="headers"
      :items="items"
      :loading="loading"
      sort-by="updated_at"
      :sort-desc="true"
      :items-per-page="itemsPerPage"
      @update:items-per-page="updateRecordsPerPage"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
      :options.sync="options"
      :server-items-length="totalItems"
      :dense="showDense"
      class="elevation-2"
    >
      <template v-slot:top>
        <v-row align="center" class="py-1 px-2" no-gutters>
          <v-col cols="12" sm="6">
            <v-breadcrumbs
              class="px-2"
              :items="breadcrumbsItems"
            ></v-breadcrumbs>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            :class="
              $store.getters.LANGUAGE == 'fa' ? 'text-left' : 'text-right'
            "
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  color="primary"
                  @click.stop="newDialog = true"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>fa-plus</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("new") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  @click.stop="getErrors"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>fa-sync</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("refresh") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  @click="showDense = !showDense"
                  v-bind="attrs"
                  v-on="on"
                  :color="showDense ? 'success' : ''"
                >
                  <v-icon small>fas fa-compress-alt</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("compactView") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  v-bind="attrs"
                  v-on="on"
                  color="orange"
                  @click="helpDialog = true"
                >
                  <v-icon small>fa-question</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("showHelp") }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </template>
      <template v-slot:item.index="{ item }">
        {{
          (options.page - 1) * options.itemsPerPage + items.indexOf(item) + 1
        }}
      </template>
      <template v-slot:item.updated_at="{ item }">
        <div class="ltr-text" v-if="$store.getters.LANGUAGE == 'fa'">
          {{ item.updated_at | moment("jYYYY/jM/jD HH:mm") }}
        </div>
        <div class="ltr-text" v-else>
          {{ item.updated_at | moment("YYYY/M/D HH:mm") }}
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              @click.stop="openEdit(item.id, item.name)"
              v-bind="attrs"
              v-on="on"
              class="mx-1"
            >
              <v-icon small>fa-edit</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("edit") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="error"
              @click.stop="confirmDelete(item.id, item.name)"
              v-bind="attrs"
              v-on="on"
              class="mx-1"
            >
              <v-icon small>fa-trash</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("delete") }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog
      v-model="newDialog"
      persistent
      @keydown.esc="closeNew"
      max-width="800"
    >
      <v-card>
        <v-form ref="newForm" v-model="newValid" @submit.prevent="tryCreate">
          <v-card-title>
            <span class="headline">{{
              $vuetify.lang.translator("createNewError", model_name)
            }}</span>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="fields.name"
                  :rules="nameRules"
                  :label="$vuetify.lang.translator('name') + ' *'"
                  type="text"
                  autocomplete="on"
                  autofocus
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="fields.code"
                  :label="$vuetify.lang.translator('code') + ' *'"
                  type="number"
                  min="0"
                  autocomplete="on"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="fields.type"
                  :label="$vuetify.lang.translator('type') + ' *'"
                  :items="allErrorTypes"
                  item-text="name"
                  item-value="id"
                ></v-select>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" type="submit" :loading="waitForNew">
              <v-icon class="mx-2">fa-save</v-icon>
              {{ $vuetify.lang.translator("create") }}
            </v-btn>
            <v-btn color="blue darken-1" text @click="closeNew">{{
              $vuetify.lang.translator("close")
            }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="editDialog"
      persistent
      @keydown.esc="closeEdit"
      max-width="640"
    >
      <v-card>
        <v-form ref="editForm" v-model="editValid" @submit.prevent="tryEdit">
          <v-card-title>
            <span class="headline">{{ dialogTitle }}</span>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editFields.name"
                  :rules="nameRules"
                  :label="$vuetify.lang.translator('name') + ' *'"
                  type="text"
                  autocomplete="on"
                  autofocus
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="editFields.code"
                  :label="$vuetify.lang.translator('code') + ' *'"
                  type="number"
                  min="0"
                  autocomplete="on"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="editFields.type"
                  :label="$vuetify.lang.translator('type') + ' *'"
                  :items="allErrorTypes"
                  item-text="name"
                  item-value="id"
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <VuePersianDatetimePicker
                  type="datetime"
                  input-class="vpdp-input"
                  :format="
                    $store.getters.LANGUAGE == 'fa'
                      ? 'jYYYY/jMM/jDD HH:mm'
                      : 'YYYY/MM/DD HH:mm'
                  "
                  input-format="YYYY-MM-DDTHH:mm:ssZ"
                  :disabled="true"
                  color="dimgray"
                  :label="$vuetify.lang.translator('createDate')"
                  v-model="created_at"
                ></VuePersianDatetimePicker>
              </v-col>

              <v-col cols="12" md="6">
                <VuePersianDatetimePicker
                  type="datetime"
                  input-class="vpdp-input"
                  :format="
                    $store.getters.LANGUAGE == 'fa'
                      ? 'jYYYY/jMM/jDD HH:mm'
                      : 'YYYY/MM/DD HH:mm'
                  "
                  input-format="YYYY-MM-DDTHH:mm:ssZ"
                  :disabled="true"
                  color="dimgray"
                  :label="$vuetify.lang.translator('updateDate')"
                  v-model="updated_at"
                ></VuePersianDatetimePicker>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" type="submit" :loading="waitForEdit">
              <v-icon class="mx-2">fa-save</v-icon
              >{{ $vuetify.lang.translator("change") }}
            </v-btn>
            <v-btn color="blue darken-1" text @click="closeEdit">{{
              $vuetify.lang.translator("close")
            }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deleteDialog"
      max-width="640px"
      persistent
      @keydown.esc="closeDelete"
    >
      <v-card>
        <v-card-title class="headline">{{ dialogTitle }}</v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="closeDelete">{{
            $vuetify.lang.translator("no")
          }}</v-btn>
          <v-btn
            color="error"
            @click="tryDelete"
            :disabled="waitForDelete"
            :loading="waitForDelete"
            >{{ $vuetify.lang.translator("yes") }}</v-btn
          >
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="helpDialog"
      persistent
      max-width="800"
      @keydown.esc="helpDialog = false"
      transition="dialog-top-transition"
    >
      <v-card>
        <v-toolbar dense elevation="2">
          <v-toolbar-title>{{
            $vuetify.lang.translator("help")
          }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="helpDialog = false">
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <div class="mt-6">
            {{ $vuetify.lang.translator("errorsHelp") }}
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="success" @click="helpDialog = false">{{
            $vuetify.lang.translator("gotIt")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { required, max } from "@/helpers/validations";
import { handleError } from "@/helpers/helper";
import VuePersianDatetimePicker from "vue-persian-datetime-picker";

export default {
  name: "Errors",
  components: {
    VuePersianDatetimePicker
  },
  data: () => ({
    breadcrumbsItems: [],
    headers: [],
    items: [],
    itemsPerPage: 0,
    options: {},
    totalItems: 0,
    newValid: false,
    editValid: false,
    fields: {
      classification_model: null,
      name: "",
      code: null,
      type: null
    },
    editFields: {
      id: null,
      classification_model: null,
      name: "",
      code: null,
      type: null
    },
    created_at: null,
    updated_at: null,
    nameRules: [],
    requiredRule: [],
    loading: true,
    newDialog: false,
    waitForNew: false,
    deleteDialog: false,
    dialogTitle: "",
    waitForDelete: false,
    idToBeDeleted: 0,
    editDialog: false,
    waitForEdit: false,
    classification_model_id: 0,
    model_name: "",
    showDense: true,
    helpDialog: false,
    allErrorTypes: []
  }),
  watch: {
    options: {
      handler() {
        this.getErrors();
      }
    },
    showDense: function() {
      this.$store.dispatch("saveShowDense", this.showDense ? "show" : "hide");
    }
  },
  created() {
    this.itemsPerPage = parseInt(this.$store.getters.PERPAGE);
    this.showDense = this.$store.getters.SHOWDENSE === "show";
    this.classification_model_id = this.$route.params.id;
    this.model_name = this.$route.params.name;

    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("dashboard"),
      to: { name: "Dashboard" }
    });
    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("management")
    });
    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("models"),
      to: { name: "Models" }
    });
    this.breadcrumbsItems.push({ text: this.model_name });
    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("errors"),
      to: {
        name: "Errors",
        params: { id: this.classification_model_id, name: this.model_name }
      }
    });

    this.headers.push({
      text: this.$vuetify.lang.translator("row"),
      value: "index",
      sortable: false,
      align: "center"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("name"),
      value: "name"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("code"),
      value: "code"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("type"),
      value: "type_title"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("updateDate"),
      value: "updated_at"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("operations"),
      value: "actions",
      sortable: false,
      align: "center"
    });

    this.allErrorTypes.push({
      id: 1,
      name: this.$vuetify.lang.translator("normal")
    });
    this.allErrorTypes.push({
      id: 2,
      name: this.$vuetify.lang.translator("warning")
    });
    this.allErrorTypes.push({
      id: 3,
      name: this.$vuetify.lang.translator("critical")
    });

    this.checkPrivileges();
  },
  methods: {
    checkPrivileges() {
      let userType = parseInt(this.$store.getters.USERTYPE);
      let allowed_privileges = [1];
      if (allowed_privileges.indexOf(userType) === -1) {
        this.$router.push({ name: "Error403" });
      }
    },
    getErrors() {
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      var parameters = null;
      if (sortBy[0]) {
        let sort_by = sortBy[0];
        if (sort_by === "type_title") {
          sort_by = "type";
        }
        parameters = {
          page: page,
          size: itemsPerPage,
          sort: (sortDesc[0] ? "-" : "") + sort_by
        };
      } else {
        parameters = {
          page: page,
          size: itemsPerPage
        };
      }
      var self = this;
      this.$http
        .get(
          "/classification/model/" + self.classification_model_id + "/error/",
          {
            params: parameters
          }
        )
        .then(function(response) {
          self.items = response.data.results;
          self.totalItems = response.data.count;
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.loading = false;
        });
    },
    tryCreate() {
      this.nameRules = [
        required(this.$vuetify.lang.translator("name"), this.$vuetify),
        max(100, this.$vuetify.lang.translator("name"), this.$vuetify)
      ];
      this.requiredRule = [
        required(this.$vuetify.lang.translator("thisField"), this.$vuetify)
      ];
      let self = this;
      setTimeout(function() {
        if (self.$refs.newForm.validate()) {
          self.waitForNew = true;
          self.fields.classification_model = self.classification_model_id;
          self.$http
            .post(
              "/classification/model/" +
                self.classification_model_id +
                "/error/",
              self.fields
            )
            .then(() => {
              self.$toasted.success(
                self.$vuetify.lang.translator("newErrorCreated"),
                {
                  duration: 5000,
                  keepOnHover: true,
                  className:
                    self.$store.getters.LANGUAGE == "fa"
                      ? "vazir-font-fd"
                      : "vazir-font",
                  iconPack: "fontawesome",
                  icon: "check",
                  position: "top-center"
                }
              );
              self.getErrors();
              self.newDialog = false;
              self.resetData();
            })
            .catch(error => {
              let result = handleError(
                error,
                self.$toasted,
                self.$router,
                self.$store
              );
              for (var item in result) {
                self.$toasted.error(result[item], {
                  duration: 5000,
                  keepOnHover: true,
                  className:
                    self.$store.getters.LANGUAGE == "fa"
                      ? "vazir-font-fd"
                      : "vazir-font",
                  iconPack: "fontawesome",
                  icon: "exclamation-triangle",
                  position: "top-center"
                });
              }
            })
            .then(function() {
              self.waitForNew = false;
            });
        }
      });
    },
    closeNew() {
      this.resetData();
      this.newDialog = false;
    },
    openEdit(id, name) {
      this.dialogTitle = this.$vuetify.lang.translator("editError", name);
      var self = this;
      this.$http
        .get("/classification/error/" + id + "/")
        .then(function(response) {
          self.editFields.id = response.data.id;
          self.editFields.classification_model =
            response.data.classification_model_id;
          self.editFields.name = response.data.name;
          self.editFields.code = response.data.code;
          self.editFields.type = response.data.type;
          self.created_at = response.data.created_at;
          self.updated_at = response.data.updated_at;
          self.editDialog = true;
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        });
    },
    tryEdit() {
      this.nameRules = [
        required(this.$vuetify.lang.translator("name"), this.$vuetify),
        max(100, this.$vuetify.lang.translator("name"), this.$vuetify)
      ];
      this.requiredRule = [
        required(this.$vuetify.lang.translator("thisField"), this.$vuetify)
      ];
      let self = this;
      setTimeout(function() {
        if (self.$refs.editForm.validate()) {
          self.waitForEdit = true;
          self.editFields.classification_model = self.classification_model_id;
          self.$http
            .put(
              "/classification/error/" + self.editFields.id + "/",
              self.editFields
            )
            .then(() => {
              self.$toasted.success(
                self.$vuetify.lang.translator("errorEdited"),
                {
                  duration: 5000,
                  keepOnHover: true,
                  className:
                    self.$store.getters.LANGUAGE == "fa"
                      ? "vazir-font-fd"
                      : "vazir-font",
                  iconPack: "fontawesome",
                  icon: "check",
                  position: "top-center"
                }
              );
              self.getErrors();
              self.editDialog = false;
              self.resetData();
            })
            .catch(error => {
              let result = handleError(
                error,
                self.$toasted,
                self.$router,
                self.$store
              );
              for (var item in result) {
                self.$toasted.error(result[item], {
                  duration: 5000,
                  keepOnHover: true,
                  className:
                    self.$store.getters.LANGUAGE == "fa"
                      ? "vazir-font-fd"
                      : "vazir-font",
                  iconPack: "fontawesome",
                  icon: "exclamation-triangle",
                  position: "top-center"
                });
              }
            })
            .then(function() {
              self.waitForEdit = false;
            });
        }
      });
    },
    closeEdit() {
      this.resetData();
      this.editDialog = false;
    },
    confirmDelete(id, name) {
      this.idToBeDeleted = id;
      this.dialogTitle = this.$vuetify.lang.translator(
        "sureToDeleteError",
        name
      );
      this.deleteDialog = true;
    },
    tryDelete() {
      if (this.idToBeDeleted === 0) {
        this.$toasted.error(this.$vuetify.lang.translator("selectAnError"), {
          duration: 5000,
          keepOnHover: true,
          className:
            self.$store.getters.LANGUAGE == "fa"
              ? "vazir-font-fd"
              : "vazir-font",
          iconPack: "fontawesome",
          icon: "exclamation-triangle",
          position: "top-center"
        });
        return;
      }
      this.waitForDelete = true;
      let self = this;
      self.$http
        .delete("/classification/error/" + this.idToBeDeleted + "/")
        .then(() => {
          self.$toasted.success(self.$vuetify.lang.translator("errorDeleted"), {
            duration: 5000,
            keepOnHover: true,
            className:
              self.$store.getters.LANGUAGE == "fa"
                ? "vazir-font-fd"
                : "vazir-font",
            iconPack: "fontawesome",
            icon: "check",
            position: "top-center"
          });
          self.getErrors();
          self.deleteDialog = false;
        })
        .catch(error => {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.waitForDelete = false;
        });
    },
    closeDelete() {
      this.idToBeDeleted = 0;
      this.deleteDialog = false;
    },
    resetData() {
      this.nameRules = [];
      this.requiredRule = [];

      this.fields.classification_model = null;
      this.fields.name = "";
      this.fields.code = null;
      this.fields.type = null;

      this.editFields.id = null;
      this.editFields.classification_model = null;
      this.editFields.name = "";
      this.editFields.code = null;
      this.editFields.type = null;

      this.created_at = null;
      this.updated_at = null;
    },
    updateRecordsPerPage(rowsPerPage) {
      this.$store.dispatch("saveRecordsPerPage", rowsPerPage);
    }
  }
};
</script>
